import { Injectable } from "@angular/core";
import { API_REQUEST_METHODS, HttpService } from "../../../ancestors/base-api.service";

/**
 * Interfaccia per il payload di un errore client da tracciare
 */
export interface ClientErrorPayload {
  errorMessage: string;
  traceId: string;
  userAgent?: string;
  clientTimestamp?: string;
  pageUrl?: string;
}

/**
 * Interfaccia per il payload di una navigazione di pagina da tracciare
 */
export interface PageViewPayload {
  route: string;
  queryParams?: Record<string, unknown>;
  traceId: string;
  userAgent?: string;
  clientTimestamp?: string;
  referrer?: string;
}

/**
 * Servizio per l'invio dei dati di audit client al backend.
 * Invia errori client e navigazioni di pagina in modo silenzioso
 * (gli errori di audit non devono impattare l'esperienza utente).
 */
@Injectable({ providedIn: "root" })
export class ClientAuditApiService extends HttpService {
  constructor() {
    super();
    this.logger.setCaller("ClientAuditApiService");
    this.createInstance({ url: this.env.apiBaseUrl });
  }

  /**
   * Invia un singolo errore client al server.
   * Metodo silenzioso: non propaga errori per non disturbare l'utente.
   */
  async sendClientError(payload: ClientErrorPayload): Promise<void> {
    try {
      await this.httpClient<{ accepted: boolean }>("/audit/client-error", {
        method: API_REQUEST_METHODS.POST,
        body: { data: payload } as unknown as Record<string, unknown>,
        noFlickering: true
      });
    } catch {
      // Errori di audit non devono impattare l'UX - logga solo un warning
      this.logger.warn("Failed to send client error audit");
    }
  }

  /**
   * Invia una singola navigazione di pagina al server.
   * Metodo silenzioso: non propaga errori per non disturbare l'utente.
   */
  async sendPageView(payload: PageViewPayload): Promise<void> {
    try {
      await this.httpClient<{ accepted: boolean }>("/audit/page-view", {
        method: API_REQUEST_METHODS.POST,
        body: { data: payload } as unknown as Record<string, unknown>,
        noFlickering: true
      });
    } catch {
      // Errori di audit non devono impattare l'UX - logga solo un warning
      this.logger.warn("Failed to send page view audit");
    }
  }
}
